import { PeriodController } from "../../calculation-base/controls/period.control";
import { TransactionView } from "@bitwarden/web-vault/app/models/view/transaction/transaction.view";
import { BalanceDatesUtils } from "@bitwarden/web-vault/app/services/store/calculation-base/balances/balanceDates.utils";
import { max, subMonths } from "date-fns";
import { PeriodType } from "@bitwarden/web-vault/app/models/types/filter.types";

/**
 * Period controller for past view with 12-month default
 * - Default start date: max 12 months ago (or first transaction if within 12 months)
 * - Default end date: the later of the option's end date (e.g. a future-dated transaction) and
 *   today, so it is never capped to today and never left behind a stale options() range
 * - Users can still manually select dates outside that range
 */
export class PastPeriodController extends PeriodController {
  private readonly DEFAULT_MONTHS_BACK = 12;

  /**
   * Options keep the full transaction range; the default SELECTED range only caps the start to
   * the last 12 months, but users can still manually select outside it. Uses the same `today`
   * for both the option derivation and the cap so the two agree even if this call straddles a
   * day boundary.
   */
  protected override deriveOptionAndDefaultSelection(transactions: readonly TransactionView[]): {
    option: PeriodType;
    defaultSelection: PeriodType;
  } {
    const today = this.today;
    const { option } = super.deriveOptionAndDefaultSelection(transactions, today);

    return {
      option,
      defaultSelection: this.capToDefaultSelection(option, today),
    };
  }

  /**
   * The same 12-month/today-capped default used on initial load, so resetting always lands on
   * the same range a fresh load would have shown.
   */
  override getDefaultSelection(): PeriodType {
    return this.capToDefaultSelection(this.options(), this.today);
  }

  private capToDefaultSelection(option: PeriodType, today: Date): PeriodType {
    const twelveMonthsAgo = BalanceDatesUtils.getStartOfDayDate(
      subMonths(today, this.DEFAULT_MONTHS_BACK),
    );

    return {
      startDate: max([option.startDate, twelveMonthsAgo]),
      endDate: max([option.endDate, today]),
      _isDefault: true,
    };
  }
}
